import type _ky from 'ky';
import type { Options } from 'ky';
import { HTTPError } from 'ky';
import Maybe from 'true-myth/maybe';
import Result from 'true-myth/result';
import { z } from 'zod';

import { HTTPErrorWithResponseBody } from './http-error-with-response-body';
import type { HTTPMethod } from './http-method';

export type RequestOptions = Pick<Options, 'body' | 'credentials' | 'headers' | 'searchParams' | 'signal' | 'timeout'>;

export type Pathname = `/${string}`;

type InternalFetchOptions = {
    readonly method: HTTPMethod;
    readonly payload?: unknown;
    readonly requestOptions: RequestOptions;
    readonly url: URL;
};

export type FetchRawResult = {
    readonly data: unknown;
    readonly status: number;
};

export type HttpFetcherOptions = {
    readonly baseUrl?: URL;
    readonly requestHeaders?: RequestHeaders;
};

export type ErrorReporter = {
    readonly reportError: (message: string, error: Error) => void;
};

type HttpFetcherDependencies = {
    readonly errorReporter: ErrorReporter;
    readonly ky: typeof _ky;
};

type ParametersWithoutPayloadBase = {
    readonly requestOptions?: RequestOptions;
};

export type ParametersWithUrl = {
    readonly url: URL;
};

export type ParametersWithPathname = {
    readonly pathname: Pathname;
};

type ParametersWithoutPayload<T extends HttpFetcherOptions> = T['baseUrl'] extends URL
    ? ParametersWithoutPayloadBase & ParametersWithPathname
    : ParametersWithoutPayloadBase & ParametersWithUrl;

type ParametersWithJsonPayload<T extends HttpFetcherOptions> = ParametersWithoutPayload<T> & {
    readonly payload: unknown;
};

export type RequestHeaders = Record<string, string>;

export const isParametersWithUrl = (
    parameters: ParametersWithPathname | ParametersWithUrl,
): parameters is ParametersWithUrl => {
    return 'url' in parameters && parameters.url instanceof URL;
};

type ErrorHandler = (error: HTTPError) => void;

export type HttpFetcherError = Error & { readonly errorCode?: string; readonly message?: string };

const backendErrorsSchema = z.object({
    errors: z
        .object({
            errorCode: z.string(),
            message: z.string(),
        })
        .array()
        .min(1),
});

const mapErrorToHttpFetcherError = (value: unknown, error: Error): HttpFetcherError => {
    const backendErrors = backendErrorsSchema.safeParse(value);

    if (backendErrors.success) {
        const errorCode = backendErrors.data.errors[0]?.errorCode ?? '';
        const errorMessage = backendErrors.data.errors[0]?.message ?? error.message;

        const message = `${errorCode}: ${errorMessage}`;

        return {
            ...error,
            errorCode,
            message,
        };
    }

    return error;
};

export interface HttpFetcher<T extends HttpFetcherOptions = HttpFetcherOptions> {
    readonly addHttpErrorHandler: (errorHandler: ErrorHandler) => void;
    readonly delete: (options: ParametersWithoutPayload<T>) => Promise<Result<unknown, HttpFetcherError>>;
    readonly deleteJson: (options: ParametersWithJsonPayload<T>) => Promise<Result<unknown, HttpFetcherError>>;
    readonly get: (options: ParametersWithoutPayload<T>) => Promise<Result<unknown, HttpFetcherError>>;
    readonly getWithStatus: (options: ParametersWithoutPayload<T>) => Promise<Result<FetchRawResult, HttpFetcherError>>;
    readonly patchJson: (options: ParametersWithJsonPayload<T>) => Promise<Result<unknown, HttpFetcherError>>;
    readonly post: (options: ParametersWithoutPayload<T>) => Promise<Result<unknown, HttpFetcherError>>;
    readonly postJson: (options: ParametersWithJsonPayload<T>) => Promise<Result<unknown, HttpFetcherError>>;
    readonly put: (options: ParametersWithoutPayload<T>) => Promise<Result<unknown, HttpFetcherError>>;
    readonly putJson: (options: ParametersWithJsonPayload<T>) => Promise<Result<unknown, HttpFetcherError>>;
    set requestHeaders(headers: RequestHeaders);
}

export const createHttpFetcher = <T extends HttpFetcherOptions>(
    { errorReporter, ky }: HttpFetcherDependencies,
    options?: T,
): HttpFetcher<T> => {
    const { baseUrl, requestHeaders: initialRequestHeaders } = options ?? {};
    let requestHeaders: Maybe<RequestHeaders> = Maybe.nothing();
    const httpErrorHandlers: ErrorHandler[] = [];

    const determineUrlToFetch = (urlParameters: ParametersWithPathname | ParametersWithUrl): URL => {
        if (isParametersWithUrl(urlParameters)) {
            return urlParameters.url;
        }

        const basePath = baseUrl?.pathname.replace(/\/+$/, '');
        const extendedPath = urlParameters.pathname.replace(/^\/+/, '');

        return new URL(`${basePath}/${extendedPath}`, baseUrl);
    };

    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: suppressed due to migration
    const fetchRaw = async (options: InternalFetchOptions): Promise<Result<FetchRawResult, HttpFetcherError>> => {
        const { method, payload, requestOptions, url } = options;
        const kyOptions: Options = {
            ...requestOptions,
            headers: requestHeaders.mapOrElse(
                () => ({ ...initialRequestHeaders, ...requestOptions.headers }),
                (defaultHeaders) => ({ ...initialRequestHeaders, ...defaultHeaders, ...requestOptions.headers }),
            ),
            json: payload,
            method,
        };

        try {
            const response = await ky(url, kyOptions);
            const contentType = response.headers.get('content-type');
            const data =
                contentType?.startsWith('application/json') === true ? await response.json() : await response.text();

            return Result.ok({ data, status: response.status });
        } catch (error: unknown) {
            if (error instanceof HTTPError) {
                errorReporter.reportError(error.message, error);

                for (const httpErrorHandler of httpErrorHandlers) {
                    httpErrorHandler(error);
                }

                try {
                    const responseJson = await error.response.json();

                    return Result.err(
                        mapErrorToHttpFetcherError(responseJson, new HTTPErrorWithResponseBody(error, responseJson)),
                    );
                } catch (_: unknown) {
                    return Result.err(error);
                }
            }

            if (error instanceof Error) {
                errorReporter.reportError(error.message, error);

                return Result.err(error);
            }
        }

        const error = new Error(`Could not fetch from ${url}`);

        errorReporter.reportError(error.message, error);

        return Result.err(error);
    };

    const fetch = async (options: InternalFetchOptions): Promise<Result<unknown, HttpFetcherError>> => {
        const result = await fetchRaw(options);

        if (result.isOk) {
            return Result.ok(result.value.data);
        }

        return result;
    };

    return {
        addHttpErrorHandler(httpErrorHandler) {
            httpErrorHandlers.push(httpErrorHandler);
        },
        async delete(parameters) {
            const { requestOptions = {} } = parameters;

            return fetch({ method: 'DELETE', requestOptions, url: determineUrlToFetch(parameters) });
        },
        async deleteJson(parameters) {
            const { payload, requestOptions = {} } = parameters;

            return fetch({ method: 'DELETE', payload, requestOptions, url: determineUrlToFetch(parameters) });
        },
        async get(parameters) {
            const { requestOptions = {} } = parameters;

            return fetch({ method: 'GET', requestOptions, url: determineUrlToFetch(parameters) });
        },
        async getWithStatus(parameters) {
            const { requestOptions = {} } = parameters;

            return fetchRaw({ method: 'GET', requestOptions, url: determineUrlToFetch(parameters) });
        },
        async patchJson(parameters) {
            const { payload, requestOptions = {} } = parameters;

            return fetch({ method: 'PATCH', payload, requestOptions, url: determineUrlToFetch(parameters) });
        },
        async post(parameters) {
            const { requestOptions = {} } = parameters;

            return fetch({ method: 'POST', requestOptions, url: determineUrlToFetch(parameters) });
        },
        async postJson(parameters) {
            const { payload, requestOptions = {} } = parameters;

            return fetch({ method: 'POST', payload, requestOptions, url: determineUrlToFetch(parameters) });
        },
        async put(parameters) {
            const { requestOptions = {} } = parameters;

            return fetch({ method: 'PUT', requestOptions, url: determineUrlToFetch(parameters) });
        },
        async putJson(parameters) {
            const { payload, requestOptions = {} } = parameters;

            return fetch({ method: 'PUT', payload, requestOptions, url: determineUrlToFetch(parameters) });
        },
        set requestHeaders(_requestHeaders: RequestHeaders) {
            requestHeaders = requestHeaders.mapOrElse(
                () => Maybe.of(_requestHeaders),
                (headers) =>
                    Maybe.of<RequestHeaders>({
                        ...headers,
                        ..._requestHeaders,
                    }),
            );
        },
    };
};
